/** SPEC-009 §85 — canonical simulation/reverse-planning warning codes. */
export const SIMULATION_WARNING_CODES = [
  'missing_conversion',
  'zero_conversion_unreachable',
  'ambiguous_reverse_merge',
  'cycle_requires_reverse_boundary',
  'period_mismatch',
  'missing_average_ticket',
  'unknown_current_value',
  'invalid_override',
  'overallocated_flow',
  'partial_merge',
  'partial_bottleneck_coverage',
  'no_bottleneck_signal',
] as const;
export type SimulationWarningCode = (typeof SIMULATION_WARNING_CODES)[number];

export interface SimulationWarning {
  code: SimulationWarningCode;
  message: string;
  refs?: readonly string[];
}

function warning(code: SimulationWarningCode, message: string, refs?: readonly string[]): SimulationWarning {
  return refs ? { code, message, refs } : { code, message };
}

export const missingConversion = (refs?: readonly string[]): SimulationWarning =>
  warning('missing_conversion', 'A required conversion rate is unknown; this requirement is unresolved.', refs);

export const zeroConversionUnreachable = (refs?: readonly string[]): SimulationWarning =>
  warning('zero_conversion_unreachable', 'This conversion is zero, so the target is unreachable through this path.', refs);

export const ambiguousReverseMerge = (refs?: readonly string[]): SimulationWarning =>
  warning('ambiguous_reverse_merge', 'Multiple incoming edges have no explicit reverse share; the requirement cannot be allocated automatically.', refs);

export const cycleRequiresReverseBoundary = (refs?: readonly string[]): SimulationWarning =>
  warning('cycle_requires_reverse_boundary', 'This path depends on a cycle; reverse planning stopped and needs a manual boundary.', refs);

export const periodMismatch = (refs?: readonly string[]): SimulationWarning =>
  warning('period_mismatch', 'Inputs use different time periods; they were not normalized automatically.', refs);

export const missingAverageTicket = (refs?: readonly string[]): SimulationWarning =>
  warning('missing_average_ticket', 'Average ticket is unknown; a revenue target cannot be converted to a client count.', refs);

export const unknownCurrentValue = (refs?: readonly string[]): SimulationWarning =>
  warning('unknown_current_value', 'The current value for this stage is unknown.', refs);

export const invalidOverride = (refs?: readonly string[]): SimulationWarning =>
  warning('invalid_override', 'This override value is invalid and was not applied.', refs);

export const overallocatedFlow = (refs?: readonly string[]): SimulationWarning =>
  warning('overallocated_flow', 'Outgoing conversions sum to more than 100%.', refs);

export const partialMerge = (refs?: readonly string[]): SimulationWarning =>
  warning('partial_merge', 'Some incoming flows are unknown; this total is partial.', refs);

export const partialBottleneckCoverage = (refs?: readonly string[]): SimulationWarning =>
  warning('partial_bottleneck_coverage', 'Some bottleneck signals are unknown for this stage; the ranking is based on partial evidence.', refs);

export const noBottleneckSignal = (refs?: readonly string[]): SimulationWarning =>
  warning('no_bottleneck_signal', 'This stage has no known signal and could not be ranked.', refs);
